import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, MessageCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { companyInfo } from '../data/mock';
import { useSiteData } from '../contexts/SiteDataContext';
import { formatWhatsAppLink, formatPhoneNumber, PHONE_NUMBER, WHATSAPP_NUMBER } from '../utils/whatsapp';

const Contact = () => {
  const { settings } = useSiteData();
  const email = settings.email || companyInfo.email;
  const address = settings.address || companyInfo.address;
  const businessHours = settings.businessHours || {};
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // In the real implementation, this would send to backend
    const whatsappMessage = `*Contato via Site*\n\nNome: ${formData.name}\nEmail: ${formData.email}\nTelefone: ${formData.phone}\nAssunto: ${formData.subject}\n\nMensagem:\n${formData.message}`;
    window.open(formatWhatsAppLink(whatsappMessage), '_blank');
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {/* Page Header */}
      <section className="bg-gradient-to-r from-[#055509] to-[#0a7a0f] text-white py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Entre em Contato</h1>
          <p className="text-xl text-gray-200">
            Nossa equipe técnica está pronta para atendê-lo
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Contact Info Cards */}
            <div className="space-y-6">
              {/* WhatsApp Card */}
              <Card className="border-2 border-[#25D366] hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#25D366] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MessageCircle className="text-white" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-[#055509] mb-2">WhatsApp</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Atendimento rápido e direto
                      </p>
                      <a
                        href={formatWhatsAppLink()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#25D366] font-semibold hover:underline text-sm"
                      >
                        {formatPhoneNumber(WHATSAPP_NUMBER)}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Phone Card */}
              <Card className="border-2 border-gray-200 hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#055509] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Phone className="text-[#FFB025]" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-[#055509] mb-2">Telefone</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Ligue para nossa central
                      </p>
                      <a
                        href={`tel:${PHONE_NUMBER}`}
                        className="text-[#055509] font-semibold hover:underline text-sm"
                      >
                        {formatPhoneNumber(PHONE_NUMBER)}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Email Card */}
              <Card className="border-2 border-gray-200 hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#055509] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Mail className="text-[#FFB025]" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-[#055509] mb-2">E-mail</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Envie sua mensagem
                      </p>
                      <a
                        href={`mailto:${email}`}
                        className="text-[#055509] font-semibold hover:underline text-sm break-all"
                      >
                        {email}
                      </a>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Address Card */}
              <Card className="border-2 border-gray-200 hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#055509] rounded-lg flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-[#FFB025]" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-[#055509] mb-2">Endereço</h3>
                      <p className="text-sm text-gray-600">
                        {address.street}<br />
                        {address.city} - {address.state}<br />
                        CEP: {address.zipcode}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Business Hours */}
              <Card className="border-2 border-[#FFB025] hover:shadow-xl transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#FFB025] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="text-[#055509]" size={24} />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-[#055509] mb-3">Horário de Atendimento</h3>
                      <div className="space-y-2 text-sm text-gray-700">
                        <div className="flex justify-between">
                          <span>Segunda a Sexta:</span>
                          <span className="font-semibold">{businessHours.monday_friday || '8h - 18h'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Sábado:</span>
                          <span className="font-semibold">{businessHours.saturday || '8h - 12h'}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Domingo:</span>
                          <span className="font-semibold">{businessHours.sunday || 'Fechado'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <Card className="border-2 border-gray-200 shadow-xl">
                <CardContent className="p-8">
                  <h2 className="text-3xl font-bold text-[#055509] mb-2">Envie sua Mensagem</h2>
                  <p className="text-gray-600 mb-8">
                    Preencha o formulário abaixo e entraremos em contato em até 2 horas
                  </p>

                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-[#055509] mb-2">
                          Nome Completo *
                        </label>
                        <Input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Seu nome"
                          className="h-12 border-gray-300"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-[#055509] mb-2">
                          Telefone *
                        </label>
                        <Input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          required
                          placeholder="(62) 99999-9999"
                          className="h-12 border-gray-300"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#055509] mb-2">
                        E-mail *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="seu@email.com"
                        className="h-12 border-gray-300"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#055509] mb-2">
                        Assunto *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        placeholder="Qual o motivo do contato?"
                        className="h-12 border-gray-300"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-[#055509] mb-2">
                        Mensagem *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        placeholder="Descreva sua necessidade ou dúvida..."
                        rows={6}
                        className="border-gray-300 resize-none"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-[#FFB025] text-[#055509] hover:bg-[#055509] hover:text-white font-bold text-lg h-14"
                    >
                      <Send className="mr-2" size={20} />
                      Enviar Mensagem via WhatsApp
                    </Button>

                    <p className="text-sm text-gray-500 text-center">
                      * Campos obrigatórios. Responderemos em até 2 horas úteis.
                    </p>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Contact;
